import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Check, Building2, Plus } from 'lucide-react';
import { tenantSwitcherService } from '@/lib/api';
import { toast } from 'sonner';

const WorkspaceSwitcher = ({ onSwitch }) => {
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadTenants = async () => {
    try {
      const res = await tenantSwitcherService.getMyTenants();
      setTenants(res.data.tenants || []);
      setActiveTenantId(res.data.active_tenant_id);
    } catch (err) {
      console.error('Error loading tenants:', err);
    }
  };

  const handleSwitch = async (tenantId) => {
    if (tenantId === activeTenantId || switching) return;
    setSwitching(true);
    try {
      await tenantSwitcherService.switchTenant(tenantId);
      setActiveTenantId(tenantId);
      setIsOpen(false);
      toast.success('Empresa cambiada');
      onSwitch?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar empresa');
    } finally {
      setSwitching(false);
    }
  };

  const activeTenant = tenants.find(t => t.id === activeTenantId);
  const filtered = tenants.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.toLowerCase().includes(search.toLowerCase())
  );

  // Don't show if only one tenant
  if (tenants.length <= 1 && !isOpen) {
    if (!activeTenant) return null;
    return (
      <div className="flex items-center gap-2.5 px-1">
        <TenantAvatar tenant={activeTenant} size="sm" />
        <span className="text-sm font-semibold text-slate-900 truncate max-w-[160px]">{activeTenant.name}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors w-full max-w-[260px]"
        data-testid="workspace-switcher"
      >
        {activeTenant && <TenantAvatar tenant={activeTenant} size="sm" />}
        <span className="text-sm font-semibold text-slate-900 truncate flex-1 text-left">
          {activeTenant?.name || 'Seleccionar empresa'}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="workspace-dropdown">
          {/* Search */}
          {tenants.length > 3 && (
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar empresa..."
                  className="w-full pl-8 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="workspace-search"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Mis Empresas</p>
          </div>

          {/* List */}
          <div className="max-h-[320px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No se encontraron empresas
              </div>
            ) : (
              filtered.map((tenant) => {
                const isActive = tenant.id === activeTenantId;
                return (
                  <button
                    key={tenant.id}
                    onClick={() => handleSwitch(tenant.id)}
                    disabled={switching}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      isActive ? 'bg-teal-50' : 'hover:bg-slate-50'
                    } ${switching ? 'opacity-50' : ''}`}
                    data-testid={`workspace-item-${tenant.id}`}
                  >
                    <TenantAvatar tenant={tenant} size="md" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isActive ? 'font-semibold text-teal-900' : 'font-medium text-slate-900'}`}>
                        {tenant.name}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">/{tenant.slug}</p>
                    </div>
                    {isActive && (
                      <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TenantAvatar = ({ tenant, size = 'sm' }) => {
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-9 h-9 text-sm';
  const color = tenant.primary_color || '#0d9488';

  if (tenant.logo_url) {
    return (
      <img
        src={tenant.logo_url}
        alt={tenant.name}
        className={`${sizeClasses} rounded-lg object-cover flex-shrink-0 border border-slate-200`}
      />
    );
  }

  const initials = tenant.name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  return (
    <div
      className={`${sizeClasses} rounded-lg flex items-center justify-center font-bold text-white flex-shrink-0`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
};

export default WorkspaceSwitcher;
